var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isPreview } from '@/config';
import { addComputedValue, useAuth, useQuestionnaire } from '@/store';
import { ActionType, UrlParamsKey } from '@/types';
import { Queue, ResolveValue, ValidateScene, getComputedAnswer, getObjListByKey, hasDuplicates, mapItemsToSectionsWithIndices, scrollPage } from '@/utils';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePreventDoubleClick, useResolveValue } from '.';
// @ts-ignore allow error for now
export const useQuestionnaireForm = (submitQuestionnaire, submitPartial, setLoading) => {
    var _a;
    const { dispatch, mapComputedValues, state: { cachedQueryAnswers, current: { state: { answers } }, nowOverride, partialSubmission, questionnaire: { entry_actions, exit_page, id, initial_pages, initial_post_submission_pages, post_submission_scenes, resolvers, scenes, sections, submission_partials } } } = useQuestionnaire();
    const { state: { currentUserPhaseSegment } } = useAuth();
    const [searchParams] = useSearchParams();
    const [resolver] = useResolveValue();
    const entryMode = searchParams.get(UrlParamsKey.ENTRY_MODE);
    const validateScene = new ValidateScene(answers, cachedQueryAnswers, currentUserPhaseSegment, resolvers, mapComputedValues, entryMode, nowOverride);
    const { current: queue } = useRef(new Queue());
    const initialPageAnswers = useRef([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isNavigationLocked, setIsNavigationLocked] = useState(false);
    const [mostForwardSectionKey, setMostForwardSection] = useState(null);
    const checkForDuplicates = () => {
        if (hasDuplicates(queue.itemsArray)) {
            throw new Error('Duplicate pages are not allowed');
        }
    };
    const handleComputedValues = () => {
        const resolvedPage = resolver.resolveCurrentQueuePage();
        const computedScene = mapComputedValues.get(resolvedPage === null || resolvedPage === void 0 ? void 0 : resolvedPage.scene_key);
        if (computedScene && resolvedPage) {
            const computedValues = computedScene
                ? [...computedScene.values()].map((x) => {
                    const value = resolver.resolveComputedValue(x.resolver);
                    return getComputedAnswer(value, x, resolvedPage);
                })
                : [];
            dispatch(addComputedValue(computedValues));
        }
    };
    const updateMostForwardSection = (sceneKey) => {
        if (sceneKey) {
            setMostForwardSection(sceneKey);
        }
        if (!queue.state.current)
            return;
        if (!sections || sections.length === 0)
            return;
        const position = queue.position;
        const sectionsMap = mapItemsToSectionsWithIndices(queue.itemsArray, sections);
        const newSection = Object.values(sectionsMap).find((section) => {
            return section.start_index <= position && position <= section.end_index;
        });
        if (mostForwardSectionKey) {
            const currentSection = sectionsMap[mostForwardSectionKey];
            if (newSection && newSection.start_index < currentSection.start_index) {
                return;
            }
        }
        setMostForwardSection(newSection.scene_key);
    };
    const resetForm = () => {
        queue.start([]);
        handleConditions(entry_actions, false);
        [...initial_pages].forEach((page) => queue.add(page));
        queue.add(exit_page);
        queue.goToIndex(0);
        checkForDuplicates();
        updateMostForwardSection();
    };
    useEffect(() => {
        // If we are in the QB preview mode and the queue position is greater than 0, we don't want to
        // start the queue again.
        if (isPreview && queue.position > 0) {
            return;
        }
        resetForm();
    }, [id]);
    useEffect(() => {
        if (submission_partials) {
            initialPageAnswers.current = getCurrentPageAnswers();
        }
        checkForDuplicates();
    }, [currentPage]);
    useEffect(() => {
        handleComputedValues();
    }, [currentPage, answers]);
    const scenesKeyList = useMemo(() => getObjListByKey([...scenes, ...post_submission_scenes], 'key'), [id]);
    const currentScene = scenesKeyList[(_a = queue.state.current) === null || _a === void 0 ? void 0 : _a.scene_key];
    const getCurrentPageAnswers = () => {
        var _a, _b, _c, _d, _e, _f;
        const repeat_index = (_b = (_a = queue.state.current) === null || _a === void 0 ? void 0 : _a.repeat_index) !== null && _b !== void 0 ? _b : null;
        const repeat_key = (_d = (_c = queue.state.current) === null || _c === void 0 ? void 0 : _c.repeat_key) !== null && _d !== void 0 ? _d : null;
        const scene_key = (_f = (_e = queue.state.current) === null || _e === void 0 ? void 0 : _e.scene_key) !== null && _f !== void 0 ? _f : '';
        return answers.filter((x) => x.page_key.scene_key === scene_key &&
            (repeat_key ? x.page_key.repeat_key === repeat_key : true) &&
            (typeof repeat_index === 'number' ? x.page_key.repeat_index === repeat_index : true));
    };
    const getCurrentAnswers = (blockKey) => {
        // TODO: Check the logic of this assignment. Do we need this?
        // Added to fix an issue with optional repeat_key in initial_pages. Answers
        // were not being set correctly for datetime fields when repeat_key was not provided.
        const { repeat_index, repeat_key } = queue.state.current;
        const repeatKey = repeat_key !== undefined ? repeat_key : repeat_key === null ? null : '';
        return answers
            .filter((x) => x.block_key === blockKey &&
            x.page_key.scene_key === (currentScene === null || currentScene === void 0 ? void 0 : currentScene.key) &&
            (repeatKey ? x.page_key.repeat_key === repeatKey : true) &&
            (typeof repeat_index === 'number' ? x.page_key.repeat_index === repeat_index : true))
            .map((x) => ({ answerKey: x.answer_key, value: x.typed_value.value }));
    };
    const canGoToNextPage = queue.canGoToNext;
    const canGoToPrevPage = queue.canGoToPrev;
    const canSubmitQuestionnaire = (currentScene === null || currentScene === void 0 ? void 0 : currentScene.key) === exit_page.scene_key;
    const pageErrors = validateScene.blocks(currentScene);
    const pageConfirmations = pageErrors.size === 0 ? validateScene.confirmations(currentScene) : [];
    const handleActions = useCallback((action, resolve) => {
        switch (action.type) {
            case ActionType.CLEAR_ALL: {
                queue.clearAllFrom();
                queue.insert(exit_page);
                break;
            }
            case ActionType.DO_NOTHING:
            case ActionType.REMOVE_FROM_QUEUE: {
                break;
            }
            case ActionType.PUSH_ITERATIONS: {
                const totalIterations = resolve.resolveInteger(action.num_iterations);
                const iterationIndices = Array.from({ length: totalIterations }, (_, i) => i).reverse();
                const sceneKeys = action.scene_keys.reverse();
                const queueList = iterationIndices.flatMap((index) => sceneKeys.map((sceneKey) => ({
                    repeat_index: index,
                    repeat_key: null,
                    scene_key: sceneKey
                })));
                queue.insert(queueList);
                break;
            }
            case ActionType.PUSH_PAGES_FROM_LIST: {
                const repeatKeys = resolve.resolveStringList(action.repeat_keys);
                const sceneKeys = action.scene_keys;
                const queueList = repeatKeys.flatMap((repeatKey) => sceneKeys.map((sceneKey) => ({
                    repeat_index: null,
                    repeat_key: repeatKey,
                    scene_key: sceneKey
                })));
                queue.insert(queueList.reverse());
                break;
            }
            case ActionType.PUSH_TO_START_OF_QUEUE: {
                const queueList = [...action.pages].reverse();
                queue.insert(queueList);
                break;
            }
            case ActionType.PUSH_WITH_INDEX: {
                const reversedSceneKeys = [...action.scene_keys].reverse();
                const index = resolve.resolveInteger(action.index);
                const queueList = reversedSceneKeys.map((sceneKey) => ({
                    repeat_index: index,
                    repeat_key: null,
                    scene_key: sceneKey
                }));
                queue.insert(queueList);
                break;
            }
        }
    }, [exit_page]);
    const handleConditions = (actions, setHistory = true) => {
        return new Promise((resolve) => {
            const resolver = new ResolveValue(answers, cachedQueryAnswers, currentUserPhaseSegment, resolvers, mapComputedValues, nowOverride);
            let isHistorySet = false;
            actions.every(({ action, condition, stop_on_match }) => {
                let found = false;
                if (resolver.resolveBoolean(condition)) {
                    found = true;
                    if (!isHistorySet && setHistory) {
                        isHistorySet = true;
                        queue.setHistory();
                    }
                    handleActions(action, resolver);
                }
                return !(stop_on_match && found);
            });
            resolve(true);
        });
    };
    const handleLoadPartial = () => {
        var _a;
        if (partialSubmission === null || partialSubmission === void 0 ? void 0 : partialSubmission.partial.webQueue) {
            //@ts-ignore allow history to have any type
            const { history, items, position } = (_a = partialSubmission === null || partialSubmission === void 0 ? void 0 : partialSubmission.partial.webQueue) !== null && _a !== void 0 ? _a : {
                history: [],
                items: {},
                position: 0
            };
            queue.start(Object.values(items), history);
            queue.goToIndex(position);
        }
    };
    const handlePostSubmission = () => {
        queue.start(initial_post_submission_pages);
        setCurrentPage(1);
    };
    const handlePartialSubmission = () => __awaiter(void 0, void 0, void 0, function* () {
        // biome-ignore lint/suspicious/noAsyncPromiseExecutor: <explanation>
        return new Promise((resolve) => __awaiter(void 0, void 0, void 0, function* () {
            try {
                if (submission_partials) {
                    const pageAnswers = getCurrentPageAnswers();
                    const isAnswersEqual = isEqual(pageAnswers, initialPageAnswers.current);
                    if (!isAnswersEqual) {
                        setLoading(true);
                        yield submitPartial();
                    }
                }
            }
            finally {
                setLoading(false);
                resolve(true);
            }
        }));
    });
    const goToNextPage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isNavigationLocked)
            return;
        if (canSubmitQuestionnaire) {
            try {
                yield submitQuestionnaire();
                handlePostSubmission();
            }
            catch (error) {
                return;
            }
        }
        if (canGoToNextPage) {
            yield handlePartialSubmission();
            if (!('button_text' in currentScene)) {
                yield handleConditions(currentScene.exit_actions);
            }
            queue.goNext();
            scrollPage();
            setCurrentPage((step) => step + 1);
            updateMostForwardSection();
        }
    }), [canGoToNextPage, getCurrentAnswers, isNavigationLocked]);
    const goToPrevPage = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (isNavigationLocked)
            return;
        if (canGoToPrevPage) {
            yield handlePartialSubmission();
            queue.goPrev();
            scrollPage();
            setCurrentPage((step) => step - 1);
            updateMostForwardSection();
        }
    }), [canGoToPrevPage, getCurrentAnswers, isNavigationLocked]);
    const goToSection = useCallback((section) => {
        const currentSectionPageKey = section.page_key.scene_key;
        const queueItemIndex = queue.itemsArray.findIndex((item) => item.scene_key === currentSectionPageKey);
        while (queueItemIndex > queue.position) {
            queue.goNext();
            setCurrentPage((step) => step + 1);
        }
        while (queueItemIndex < queue.position) {
            queue.goPrev();
            setCurrentPage((step) => step - 1);
        }
    }, []);
    return [
        currentScene,
        {
            canGoToNextPage,
            canGoToPrevPage,
            canSubmitQuestionnaire,
            currentPage,
            getCurrentAnswers,
            getCurrentPageAnswers,
            goToNextPage: usePreventDoubleClick(goToNextPage),
            goToPrevPage: usePreventDoubleClick(goToPrevPage),
            goToSection,
            handleLoadPartial,
            isNavigationLocked,
            mostForwardSectionKey,
            pageConfirmations,
            pageErrors,
            pageIsValid: pageErrors.size === 0,
            queue,
            resetForm,
            setNavigationLock: setIsNavigationLocked,
            updateMostForwardSection
        }
    ];
};
export default useQuestionnaireForm;
